import React from "react";
import {
  Card,
  Typography,
  Box,
  IconButton,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
  Avatar,
  Grid,
  Paper,
  Chip,
  Fade,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { FaWhatsapp } from "react-icons/fa";
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const ListData = ({ contactData, onDeletePress, onProfilePress, viewMode = 'list' }) => {
  const { isDark } = useCustomTheme();
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const formatPhoneNumber = (phoneObj) => {
    if (!phoneObj || !phoneObj.phone) return "";
    const phone = phoneObj.phone.toString();
    // Remove any non-digit characters
    return phone.replace(/\D/g, '');
  };

  const handleDial = (phoneObj, e) => {
    e.stopPropagation(); // Prevent card click
    if (!phoneObj || !phoneObj.phone) {
      setSnackbar({
        open: true,
        message: "No phone number available",
        severity: "error",
      });
      return;
    }
    const formattedPhone = formatPhoneNumber(phoneObj);
    window.location.href = `tel:${formattedPhone}`;
  };

  const handleWhatsApp = (phoneObj, e) => {
    e.stopPropagation(); // Prevent card click
    if (!phoneObj || !phoneObj.phone) {
      setSnackbar({
        open: true,
        message: "No phone number available",
        severity: "error",
      });
      return;
    }
    const formattedPhone = formatPhoneNumber(phoneObj);
    const message = "Hi! I am reaching out to you.";
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleShare = (contact, e) => {
    e.stopPropagation(); // Prevent card click
    if (!contact) {
      setSnackbar({
        open: true,
        message: "No contact information available to share",
        severity: "error",
      });
      return;
    }

    const shareData = {
      title: `Contact: ${contact?.fullName || 'Contact'}`,
      text: `
Name: ${contact?.fullName || 'N/A'}
${contact?.designation ? `Designation: ${contact.designation}` : ''}
${contact?.orgName ? `Organization: ${contact.orgName}` : ''}
${contact?.phones?.[0]?.phone ? `Phone: ${contact.phones[0].phone}` : ''}
${contact?.emails?.[0]?.email ? `Email: ${contact.emails[0].email}` : ''}
${contact?.website ? `Website: ${contact.website}` : ''}
${contact?.address ? `Address: ${contact.address}` : ''}
`.trim()
    };

    if (navigator.share) {
      navigator.share(shareData)
        .then(() => {
          setSnackbar({
            open: true,
            message: "Contact shared successfully!",
            severity: "success"
          });
        })
        .catch((err) => {
          console.error("Error sharing:", err);
          copyToClipboard(shareData.text);
        });
    } else {
      copyToClipboard(shareData.text);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setSnackbar({
          open: true,
          message: "Contact information copied to clipboard!",
          severity: "success"
        });
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        setSnackbar({
          open: true,
          message: "Failed to copy contact information",
          severity: "error"
        });
      });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 2: return 'error.main';
      case 1: return 'warning.main';
      case 0: return 'success.main';
      case -1: return 'text.disabled';
      default: return 'text.disabled';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 2: return 'Very High';
      case 1: return 'High';
      case 0: return 'Normal';
      case -1: return 'Low';
      default: return 'Normal';
    }
  };

  // Grid View Component
  const GridCard = ({ card, index }) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={card.id}>
      <Fade in timeout={300 + index * 100}>
        <Card
          sx={{
            height: '100%',
            display: "flex",
            flexDirection: "column",
            background: isDark 
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-8px) scale(1.02)",
              boxShadow: isDark 
                ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            },
          }}
          onClick={() => onProfilePress(card.id)}
        >
          {/* Header with Avatar and Actions */}
          <Box sx={{ position: 'relative', p: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                src={card.imgUrl}
                sx={{
                  width: 56,
                  height: 56,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontSize: 24,
                }}
              >
                {!card.imgUrl && <BusinessIcon />}
              </Avatar>
              
              <Box sx={{ ml: 2, flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {card.fullName || "No Name"}
                </Typography>
                {card.designation && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {card.designation}
                  </Typography>
                )}
              </Box>

              {/* Priority Indicator */}
              {card.priority > 0 && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: 16 }} />}
                  label={getPriorityLabel(card.priority)}
                  size="small"
                  sx={{
                    bgcolor: getPriorityColor(card.priority) + '20',
                    color: getPriorityColor(card.priority),
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>

            {/* Company Info */}
            {card.orgName && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WorkIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {card.orgName}
                </Typography>
              </Box>
            )}

            {/* Delete Button */}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onDeletePress(card.id);
              }}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: 'error.main',
                color: 'white',
                width: 32,
                height: 32,
                '&:hover': { bgcolor: 'error.dark' },
              }}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ p: 2, pt: 0, mt: 'auto' }}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: 'center' }}>
              <Tooltip title="Call">
                <IconButton
                  onClick={(e) => handleDial(card.phones?.[0], e)}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: "white",
                    "&:hover": { 
                      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <PhoneIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="WhatsApp">
                <IconButton
                  onClick={(e) => handleWhatsApp(card.phones?.[0], e)}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                    color: "white",
                    "&:hover": { 
                      background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <FaWhatsapp size={16} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Share Contact">
                <IconButton
                  onClick={(e) => handleShare(card, e)}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: "white",
                    "&:hover": { 
                      background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Card>
      </Fade>
    </Grid>
  );

  // List View Component  
  const ListCard = ({ card, index }) => (
    <Fade in timeout={200 + index * 50} key={card.id}>
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          p: 2,
          background: isDark 
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          cursor: "pointer",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: isDark 
              ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
              : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          },
          position: 'relative',
        }}
        onClick={() => onProfilePress(card.id)}
      >
        {/* Avatar */}
        <Avatar
          src={card.imgUrl}
          sx={{
            width: 64,
            height: 64,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            mr: 2,
            fontSize: 28,
          }}
        >
          {!card.imgUrl && <BusinessIcon />}
        </Avatar>

        {/* Contact Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
              {card.fullName || "No Name"}
            </Typography>
            {card.priority > 0 && (
              <StarIcon sx={{ fontSize: 18, color: getPriorityColor(card.priority) }} />
            )}
          </Box>
          
          {card.designation && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <WorkIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              {card.designation}
            </Typography>
          )}
          
          {card.orgName && (
            <Typography variant="body2" color="text.secondary">
              <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              {card.orgName}
            </Typography>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
          <Tooltip title="Call">
            <IconButton
              onClick={(e) => handleDial(card.phones?.[0], e)}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: "white",
                "&:hover": { 
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <PhoneIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="WhatsApp">
            <IconButton
              onClick={(e) => handleWhatsApp(card.phones?.[0], e)}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                color: "white",
                "&:hover": { 
                  background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <FaWhatsapp size={16} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Share Contact">
            <IconButton
              onClick={(e) => handleShare(card, e)}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: "white",
                "&:hover": { 
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Delete Button */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDeletePress(card.id);
            }}
            size="small"
            sx={{
              bgcolor: 'error.main',
              color: 'white',
              ml: 1,
              '&:hover': { 
                bgcolor: 'error.dark',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Card>
    </Fade>
  );

  return (
    <>
      {viewMode === 'grid' ? (
        <Grid container spacing={2} sx={{ '& > *:last-child': { mb: 2 } }}>
          {contactData?.contacts?.map((card, index) => (
            <GridCard key={card.id} card={card} index={index} />
          ))}
        </Grid>
      ) : (
        <Box sx={{ '& > *:last-child': { mb: 2 } }}>
          {contactData?.contacts?.map((card, index) => (
            <ListCard key={card.id} card={card} index={index} />
          ))}
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity || "info"} 
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default React.memo(ListData);
